import React, { useState, useMemo } from 'react';
import { WS } from './workspaceData.ts';

interface PromptIDEProps {
  systemPrompt: string;
  setSystemPrompt: (v: string) => void;
  skillPromptInjections?: { id: string; name: string; prompt?: string }[];
}

// Very small markdown-ish renderer for preview (headings, lists, code, bold, italics)
function simpleMarkdownToHtml(md: string) {
  if (!md) return '';
  let out = md
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/^### (.*$)/gim, '<h3>$1</h3>')
    .replace(/^## (.*$)/gim, '<h2>$1</h2>')
    .replace(/^# (.*$)/gim, '<h1>$1</h1>')
    .replace(/\*\*(.*?)\*\*/gim, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/gim, '<em>$1</em>')
    .replace(/`{3}([\s\S]*?)`{3}/gim, '<pre><code>$1</code></pre>')
    .replace(/`([^`]+)`/gim, '<code>$1</code>')
    .replace(/(^|\n)\- (.*?)(?=\n|$)/gim, '$1<li>$2</li>')
    .replace(/(<li>.*?<\/li>)/gim, '<ul>$1</ul>');
  // paragraphs
  out = out.replace(/(^|\n)([^<\n][^\n]*)(\n|$)/gim, (m, p1, p2) => {
    if (/^<h|^<ul|^<pre|^<code|^<blockquote/.test(p2.trim())) return m;
    return `${p1}<p>${p2.trim()}</p>${m.endsWith('\n') ? '' : ''}`;
  });
  return out;
}

export default function PromptIDE({ systemPrompt, setSystemPrompt, skillPromptInjections = [] }: PromptIDEProps) {
  const [mode, setMode] = useState<'edit' | 'preview'>('edit');

  const lines = systemPrompt.split('\n');
  const charCount = systemPrompt.length;

  const previewHtml = useMemo(() => {
    const combined = [systemPrompt, '', ...skillPromptInjections.map(s => `### ${s.name}\n${s.prompt || ''}`)].join('\n');
    return simpleMarkdownToHtml(combined);
  }, [systemPrompt, skillPromptInjections]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: WS.surface2 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flex: 1 }}>
          <button onClick={() => setMode('edit')} style={{ fontSize: 13, padding: '6px 10px', borderRadius: 8, background: mode === 'edit' ? WS.surface3 : 'transparent', border: 'none', cursor: 'pointer', color: WS.ink }}>Edit</button>
          <button onClick={() => setMode('preview')} style={{ fontSize: 13, padding: '6px 10px', borderRadius: 8, background: mode === 'preview' ? WS.surface3 : 'transparent', border: 'none', cursor: 'pointer', color: WS.ink4 }}>Preview</button>
        </div>
        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: WS.ink4 }}>{charCount.toLocaleString()} chars</div>
      </div>

      <div style={{ display: 'flex', minHeight: 320, background: WS.surface, borderTop: `1px solid ${WS.border}` }}>
        {mode === 'edit' && (
          <div style={{ display: 'flex', width: '100%' }}>
            <div style={{ width: 46, background: WS.surface2, borderRight: `1px solid ${WS.border}`, padding: '12px 8px', color: WS.ink4, fontSize: 12, textAlign: 'right', fontFamily: "'JetBrains Mono', monospace" }}>
              {lines.map((_, i) => <div key={i} style={{ height: 20, lineHeight: '20px' }}>{i + 1}</div>)}
            </div>
            <textarea
              value={systemPrompt}
              onChange={e => setSystemPrompt(e.target.value)}
              placeholder={`You are a helpful AI assistant specialized in…\n\n# Identity\nYou are…\n\n# Capabilities\n- Search\n- Analyze\n\n# Constraints\n- Never hallucinate`}
              style={{ flex: 1, padding: '12px 16px', border: 'none', outline: 'none', background: 'transparent', color: WS.ink, fontFamily: "'JetBrains Mono', monospace", fontSize: 13.5, lineHeight: 1.8, resize: 'none' }}
            />
          </div>
        )}

        {mode === 'preview' && (
          <div style={{ padding: 16, overflowY: 'auto', width: '100%' }}>
            <div dangerouslySetInnerHTML={{ __html: previewHtml }} style={{ color: WS.ink, fontSize: 14, lineHeight: 1.7 }} />
          </div>
        )}
      </div>
    </div>
  );
}
